import {
  getApiPublicPaymentsToken,
  getApiPublicQuoteSelectionsToken,
  postApiPublicQuoteSelectionsTokenDecision,
} from './apiClient.gen'
import { fetcher } from './fetcher'
import { useApiFetch } from '@/composables/useApiFetch'

const unwrap = <T>(value: unknown): T => ((value as any)?.data ?? value) as T

export interface PaymentLinkInfo {
  paymentLinkId: string
  token: string
  clientAccountId?: string | null
  policyId?: string | null
  amountCents?: number | null
  currencyCode?: string | null
  description?: string | null
  clientContext?: string | null
  customerEmail?: string | null
  maxUses: number
  uses: number
  expiresAt?: string | null
  paymentStatus?: string | null
  paidAtUtc?: string | null
  stripeCheckoutSessionId?: string | null
  stripePaymentIntentId?: string | null
  teamId?: string | null
  isValid: boolean
  [key: string]: unknown
}

export interface PaymentLinkCheckoutSession {
  id?: string
  url?: string
  paymentLinkId?: string
  token?: string
  clientAccountId?: string | null
  policyId?: string | null
  amountCents?: number | null
  currencyCode?: string | null
  description?: string | null
  clientContext?: string | null
  customerEmail?: string | null
  teamId?: string
  status?: string
  [key: string]: unknown
}

export type PublicLinkType = 'intake' | 'payment'

export type QuoteSelectionDecisionType = 'APPROVED' | 'QUESTION' | 'DECLINED'

export interface PublicQuoteSelectionInfo {
  isValid: boolean
  canRespond: boolean
  invalidReason?: string | null
  quoteSelectionRequestId?: string
  teamId?: string
  quoteId?: string
  submissionId?: string
  clientAccountId?: string
  carrierPartyId?: string
  carrierName?: string | null
  accountName?: string | null
  quoteNumber?: string | null
  lobCode?: string
  insuranceTypeCode?: string | null
  effectiveFrom?: string
  effectiveTo?: string
  quoteStatusCode?: string
  annualPremium?: number | null
  requiresESign?: boolean
  eSignStatus?: string
  recipientEmail?: string
  recipientName?: string | null
  subject?: string | null
  messageBody?: string | null
  status?: string
  decisionType?: string | null
  decisionNotes?: string | null
  signedName?: string | null
  expiresAt?: string
  requestedAt?: string
  viewedAt?: string | null
  respondedAt?: string | null
  [key: string]: unknown
}

export interface AccessLinkResolution {
  token: string
  linkType: PublicLinkType
  teamId: string
  subjectId: string
  authPolicy: string
  expiresAt?: string | null
  maxUses: number
  uses: number
  revokedAt?: string | null
  isValid: boolean
  isExpired: boolean
  isExhausted: boolean
  isRevoked: boolean
  clientRoute?: string | null
  [key: string]: unknown
}

export function usePublicLinkApi() {
  const api = useApiFetch()

  async function getPaymentLink(token: string): Promise<PaymentLinkInfo> {
    const response = await getApiPublicPaymentsToken(token)
    return unwrap<PaymentLinkInfo>(response)
  }

  async function createPaymentLinkCheckoutSession(
    token: string,
    payload?: {
      successUrl?: string | null
      cancelUrl?: string | null
      customerEmail?: string | null
    },
  ): Promise<PaymentLinkCheckoutSession> {
    const value = String(token || '').trim()
    if (!value) throw new Error('Payment token is required')

    return await fetcher<PaymentLinkCheckoutSession>(
      `/api/public/payments/${encodeURIComponent(value)}/checkout-session`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          successUrl: payload?.successUrl ?? null,
          cancelUrl: payload?.cancelUrl ?? null,
          customerEmail: payload?.customerEmail ?? null,
        }),
      },
    )
  }

  async function resolveAccessLink(token: string): Promise<AccessLinkResolution | null> {
    const value = String(token || '').trim()
    if (!value) throw new Error('Link token is required')

    const response = await api.apiFetch(`/api/public/links/${encodeURIComponent(value)}/resolve`, {
      method: 'POST',
    })
    if (response.status === 404) return null
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(text || `HTTP ${response.status}`)
    }

    return (await response.json()) as AccessLinkResolution
  }

  async function getQuoteSelection(token: string): Promise<PublicQuoteSelectionInfo> {
    const value = String(token || '').trim()
    if (!value) throw new Error('Selection token is required')
    const response = await getApiPublicQuoteSelectionsToken(value)
    return unwrap<PublicQuoteSelectionInfo>(response)
  }

  async function submitQuoteSelectionDecision(
    token: string,
    payload: {
      decisionType: QuoteSelectionDecisionType
      decisionNotes?: string | null
      signedName?: string | null
    },
  ): Promise<PublicQuoteSelectionInfo> {
    const value = String(token || '').trim()
    if (!value) throw new Error('Selection token is required')
    const response = await postApiPublicQuoteSelectionsTokenDecision(value, {
      decisionType: payload.decisionType,
      decisionNotes: payload.decisionNotes ?? null,
      signedName: payload.signedName ?? null,
    })
    return unwrap<PublicQuoteSelectionInfo>(response)
  }

  return {
    getPaymentLink,
    createPaymentLinkCheckoutSession,
    resolveAccessLink,
    getQuoteSelection,
    submitQuoteSelectionDecision,
  }
}
